import React, { useState } from "react";
import { format, addDays, isWeekend, startOfToday } from "date-fns";
import { de } from "date-fns/locale";
import { CalendarDays, PhoneCall, X } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Checkbox } from "./ui/checkbox";

export const TIME_SLOTS = [
  { value: "vormittags", label: "Vormittags", hint: "8 bis 12 Uhr" },
  { value: "mittags", label: "Mittags", hint: "12 bis 14 Uhr" },
  { value: "nachmittags", label: "Nachmittags", hint: "14 bis 18 Uhr" },
];

export const CallbackPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const { callback_requested, callback_date, callback_time } = value;
  const selected = callback_date ? new Date(callback_date) : undefined;

  return (
    <div
      className={`rounded-2xl border transition-colors ${
        callback_requested ? "border-brand-300 bg-white" : "border-brand-100 bg-white/60"
      }`}
      data-testid="callback-block"
    >
      <label className="flex items-center gap-3 p-4 cursor-pointer select-none">
        <Checkbox
          data-testid="callback-checkbox"
          checked={callback_requested}
          onCheckedChange={(v) =>
            onChange({ callback_requested: Boolean(v), callback_date: "", callback_time: "" })
          }
          className="data-[state=checked]:bg-brand-900 data-[state=checked]:border-brand-900"
        />
        <span className="inline-flex items-center gap-2 text-[14px] font-medium text-brand-900">
          <PhoneCall className="w-4 h-4 text-accent2" />
          Ich wünsche einen Rückruf
        </span>
        <span className="ml-auto text-[12px] text-brand-500 hidden sm:inline">optional</span>
      </label>

      {callback_requested && (
        <div className="px-4 pb-4 grid sm:grid-cols-2 gap-4 animate-fade-up" data-testid="callback-details">
          <div>
            <div className="text-[13px] font-medium text-brand-800 mb-1.5">Wunschtermin</div>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="callback-date-trigger"
                  className="w-full h-12 px-4 rounded-md border border-brand-200 bg-white text-left text-[14px] inline-flex items-center gap-2 hover:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <CalendarDays className="w-4 h-4 text-brand-500" />
                  {selected ? (
                    <span className="text-brand-900">{format(selected, "EEEE, d. MMMM yyyy", { locale: de })}</span>
                  ) : (
                    <span className="text-brand-400">Datum wählen (Mo. bis Fr.)</span>
                  )}
                  {selected && (
                    <X
                      className="w-4 h-4 ml-auto text-brand-400 hover:text-brand-900"
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange({ ...value, callback_date: "" });
                      }}
                    />
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  locale={de}
                  weekStartsOn={1}
                  selected={selected}
                  onSelect={(d) => {
                    onChange({ ...value, callback_date: d ? format(d, "yyyy-MM-dd") : "" });
                    setOpen(false);
                  }}
                  disabled={(d) => d < addDays(startOfToday(), 1) || isWeekend(d)}
                  fromDate={addDays(startOfToday(), 1)}
                  toDate={addDays(startOfToday(), 60)}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <div className="text-[13px] font-medium text-brand-800 mb-1.5">Uhrzeit</div>
            <div className="grid grid-cols-3 gap-2" role="radiogroup">
              {TIME_SLOTS.map((t) => {
                const active = callback_time === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-testid={`callback-time-${t.value}`}
                    onClick={() => onChange({ ...value, callback_time: active ? "" : t.value })}
                    className={`h-12 rounded-md border text-[13px] leading-tight transition-colors ${
                      active
                        ? "bg-brand-900 border-brand-900 text-white"
                        : "bg-white border-brand-200 text-brand-800 hover:border-brand-400"
                    }`}
                  >
                    <div className="font-medium">{t.label}</div>
                    <div className={`text-[11px] ${active ? "text-brand-200" : "text-brand-500"}`}>{t.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
